import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { List, ChevronDownIcon, Check } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuPortal,
    DropdownMenuSubContent,
} from "../../../../../../components/ui/dropdown-menu";
import { Switch } from "./../../../../../../components/ui/switch";
import Button from "../../../../../../components/shared/Button";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useColumns } from "../../../hooks/grid/useColumns";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../constants/scheduleTypes";

export default function ViewOptions() {
    const { t } = useTranslation();
    const [, setSearchParams] = useSearchParams();
    const [{ mode, viewBy }] = useFilters();
    const {
        dispatch,
        dailyInfo,
        weeklySummary,
        columns,
        type,
    } = useScheduleContext();
    const _columns = useColumns();
    const hideSummary = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hideSummary`);
    const hideDailyInfo = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hideDailyInfo`);

    const onDailyInfoChange = useCallback((value: boolean) => {
        dispatch({ type: "daily-info", payload: value });
    }, [dispatch]);

    const onWeeklySummaryChange = useCallback((value: boolean) => {
        dispatch({ type: "weekly-summary", payload: value });
    }, [dispatch]);

    const onColumnsChange = useCallback((value: string) => {
        // dispatch({ type: "view-by" });
        dispatch({ type: "columns", payload: { ...columns, [value]: !columns[value as any] } });
    }, [columns, dispatch]);

    const onViewByChange = useCallback((value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("viewBy", value);
            return next;
        });
        const defaultState = _columns.reduce((acc: any, col: any) => {
            acc[col.field] = col.field === "name" ? true : false;
            return acc;
        }, {});
        dispatch({ type: "columns", payload: defaultState });
    }, [_columns, dispatch, setSearchParams]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="tw-font-normal tw-px-4 tw-text-neutral-900 tw-h-9">
                    {t("view-options")}
                    <ChevronDownIcon />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="tw-w-56 tw-bg-white tw-px-5 tw-py-3">
                {mode !== "month" && _columns.map((column: any) => (
                    <DropdownMenuItem
                        key={column.field}
                        className="tw-cursor-pointer"
                        onSelect={() => onViewByChange(column.field)}
                        hidden={["my-profile", "profile"].includes(type)}
                    >
                        {t("view-by", { option: column?.viewOptionLabel || column.text })}
                        {(viewBy || "name") === column.field ? (
                            <Check className="tw-ml-auto tw-h-4 tw-w-4" />
                        ) : (
                            <span className="tw-ml-auto tw-h-4 tw-w-4" />
                        )}
                    </DropdownMenuItem>
                ))}

                {mode !== "month" && <DropdownMenuSeparator />}

                <DropdownMenuItem
                    className="tw-cursor-pointer"
                    onSelect={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        dispatch({ type: "list-view", payload: { type: "all" } });
                    }}
                    hidden={!["team", "home"].includes(type)}
                >
                    <List className="tw-mr-2 tw-h-4 tw-w-4" />
                    <span>{t("list-view")}</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator hidden={!["team", "home"].includes(type)} />


                <DropdownMenuSub>
                    <DropdownMenuSubTrigger hidden={mode === "month"}>
                        <span>{t("column")}</span>
                    </DropdownMenuSubTrigger>

                    <DropdownMenuPortal>
                        <DropdownMenuSubContent className="tw-bg-white tw-px-2">
                            {_columns.map((column: any) => (
                                <DropdownMenuItem
                                    key={column.field}
                                    className="tw-cursor-pointer"
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onColumnsChange(column.field);
                                    }}
                                    disabled={column.disabled}
                                >
                                    <span>{column.text}</span>
                                    {columns?.[column.field] || !column.hide ? (
                                        <Check className="tw-ml-auto tw-h-4 tw-w-4" />
                                    ) : (
                                        <span className="tw-ml-auto tw-h-4 tw-w-4" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                </DropdownMenuSub>

                <DropdownMenuSeparator hidden={mode === "month" || (hideDailyInfo && hideSummary)} />

                <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    hidden={!["team", "home"].includes(type) || hideDailyInfo}
                >
                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-cursor-pointer">
                        <span>{t("daily-info")}</span>
                        <Switch
                            id="daily-info"
                            className="tw-cursor-pointer"
                            checked={dailyInfo}
                            onCheckedChange={onDailyInfoChange}
                        />
                    </div>
                </DropdownMenuItem>

                <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    hidden={!(["day", "week", "custom", "pay-group"].includes(mode) && ["team", "home"].includes(type)) || hideSummary}
                >
                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-cursor-pointer">
                        <span>
                            {mode === "week"
                                ? t("weekly-summary")
                                : mode === "day"
                                    ? t("daily-summary")
                                    : t("summary")}
                        </span>
                        <Switch
                            id="weekly-summary"
                            className="tw-cursor-pointer"
                            checked={weeklySummary}
                            onCheckedChange={onWeeklySummaryChange}
                        />
                    </div>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
